import { api } from '../net/api';

// Account / auth UI: login, register, and account management, wired to the backend.
// Notifies the app via onChange when the auth state changes (so it can sync + refresh).

export interface AccountCallbacks {
  onChange(loggedIn: boolean, username: string | null): void;
  onBack(): void;
}

export class AccountUI {
  private root: HTMLElement;
  private cb: AccountCallbacks;
  private auth!: HTMLElement;
  private account!: HTMLElement;
  private mode: 'login' | 'register' = 'login';

  constructor(root: HTMLElement, cb: AccountCallbacks) {
    this.root = root;
    this.cb = cb;
    this.build();
  }

  private build(): void {
    const wrap = document.createElement('div');
    wrap.innerHTML = `
      <div id="auth" class="overlay hidden">
        <div class="panel" style="max-width:380px">
          <h2 id="auth-title">Log In</h2>
          <div class="field"><label>Username</label><input id="auth-user" autocomplete="username"/></div>
          <div class="field" id="auth-email-field" style="display:none"><label>Email</label><input id="auth-email" type="email" autocomplete="email"/></div>
          <div class="field"><label>Password</label><input id="auth-pass" type="password" autocomplete="current-password"/></div>
          <div class="auth-status" id="auth-status"></div>
          <div class="actions"><button class="menu-btn" id="auth-submit">Log In</button></div>
          <div class="auth-toggle" id="auth-toggle"></div>
          <div class="auth-toggle"><a id="auth-forgot">Forgot password?</a></div>
          <div class="actions"><button class="menu-btn" id="auth-back" style="font-size:18px">Back</button></div>
        </div>
      </div>
      <div id="account" class="overlay hidden">
        <div class="panel" style="max-width:420px">
          <h2>Account</h2>
          <div class="account-name" id="acc-name"></div>
          <div class="auth-status" id="acc-status"></div>

          <div class="field"><label>Change username</label><input id="acc-newuser"/></div>
          <div class="actions"><button class="menu-btn" id="acc-changeuser" style="font-size:18px">Update username</button></div>

          <div class="field"><label>Current password</label><input id="acc-curpass" type="password"/></div>
          <div class="field"><label>New password</label><input id="acc-newpass" type="password"/></div>
          <div class="actions"><button class="menu-btn" id="acc-changepass" style="font-size:18px">Update password</button></div>

          <div class="actions" style="margin-top:18px">
            <button class="menu-btn" id="acc-logout" style="font-size:18px">Log Out</button>
            <button class="menu-btn" id="acc-delete" style="font-size:18px;color:#e58a72">Delete</button>
            <button class="menu-btn" id="acc-back" style="font-size:18px">Back</button>
          </div>
        </div>
      </div>
    `;
    this.root.appendChild(wrap);
    this.auth = this.root.querySelector('#auth')!;
    this.account = this.root.querySelector('#account')!;

    const $ = <T extends HTMLElement = HTMLElement>(id: string) => this.root.querySelector<T>('#' + id)!;
    $('auth-submit').onclick = () => this.submitAuth();
    $('auth-back').onclick = () => this.close();
    $('acc-back').onclick = () => this.close();
    $('auth-toggle').onclick = (e) => {
      if ((e.target as HTMLElement).id === 'auth-switch') this.setMode(this.mode === 'login' ? 'register' : 'login');
    };
    $('auth-forgot').onclick = () => this.forgot();
    $('acc-logout').onclick = () => this.logout();
    $('acc-changeuser').onclick = () => this.changeUsername();
    $('acc-changepass').onclick = () => this.changePassword();
    $('acc-delete').onclick = () => this.deleteAccount();
    $<HTMLInputElement>('auth-pass').addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') this.submitAuth();
    });
    this.setMode('login');
  }

  private hide(): void {
    this.auth.classList.add('hidden');
    this.account.classList.add('hidden');
  }

  async open(): Promise<void> {
    const s = await api.session();
    if (s.authenticated && s.username) this.showAccount(s.username);
    else this.showAuth();
  }

  private close(): void {
    this.hide();
    this.cb.onBack();
  }

  private showAuth(): void {
    this.hide();
    this.setMode('login');
    this.setStatus('');
    this.auth.classList.remove('hidden');
  }

  private showAccount(username: string): void {
    this.hide();
    (this.root.querySelector('#acc-name') as HTMLElement).textContent = username;
    (this.root.querySelector('#acc-status') as HTMLElement).textContent = '';
    this.account.classList.remove('hidden');
  }

  private setMode(mode: 'login' | 'register'): void {
    this.mode = mode;
    const isReg = mode === 'register';
    (this.root.querySelector('#auth-title') as HTMLElement).textContent = isReg ? 'Create Account' : 'Log In';
    (this.root.querySelector('#auth-submit') as HTMLElement).textContent = isReg ? 'Create Account' : 'Log In';
    (this.root.querySelector('#auth-email-field') as HTMLElement).style.display = isReg ? 'flex' : 'none';
    (this.root.querySelector('#auth-toggle') as HTMLElement).innerHTML = isReg
      ? `Have an account? <a id="auth-switch">Log in</a>`
      : `No account? <a id="auth-switch">Create one</a>`;
    this.setStatus('');
  }

  private setStatus(msg: string, error = false): void {
    const el = this.root.querySelector('#auth-status') as HTMLElement;
    el.textContent = msg;
    el.classList.toggle('error', error);
  }
  private setAccStatus(msg: string, error = false): void {
    const el = this.root.querySelector('#acc-status') as HTMLElement;
    el.textContent = msg;
    el.classList.toggle('error', error);
  }

  private val(id: string): string {
    return (this.root.querySelector('#' + id) as HTMLInputElement).value.trim();
  }

  private async submitAuth(): Promise<void> {
    const username = this.val('auth-user');
    const password = (this.root.querySelector('#auth-pass') as HTMLInputElement).value;
    if (!username || !password) return this.setStatus('Enter a username and password.', true);
    this.setStatus('Please wait…');
    const res =
      this.mode === 'register'
        ? await api.register(username, this.val('auth-email'), password)
        : await api.login(username, password);
    if (res.success) {
      this.setStatus('Welcome!');
      this.cb.onChange(true, username);
      this.showAccount(username);
    } else {
      this.setStatus(res.message ?? 'Failed.', true);
    }
  }

  private async forgot(): Promise<void> {
    const email = prompt('Enter your account email to receive a reset link:');
    if (!email) return;
    await api.requestReset(email.trim());
    this.setStatus('If that email exists, a reset link has been sent.');
  }

  private async logout(): Promise<void> {
    await api.logout();
    this.cb.onChange(false, null);
    this.showAuth();
  }

  private async changeUsername(): Promise<void> {
    const newUsername = this.val('acc-newuser');
    if (!newUsername) return this.setAccStatus('Enter a new username.', true);
    const res = await api.changeUsername(newUsername);
    if (res.success) {
      this.setAccStatus('Username updated.');
      (this.root.querySelector('#acc-name') as HTMLElement).textContent = newUsername;
      this.cb.onChange(true, newUsername);
    } else this.setAccStatus(res.message ?? 'Failed.', true);
  }

  private async changePassword(): Promise<void> {
    const cur = (this.root.querySelector('#acc-curpass') as HTMLInputElement).value;
    const next = (this.root.querySelector('#acc-newpass') as HTMLInputElement).value;
    if (!cur || !next) return this.setAccStatus('Enter both passwords.', true);
    const res = await api.changePassword(cur, next);
    this.setAccStatus(res.success ? 'Password updated.' : res.message ?? 'Failed.', !res.success);
  }

  private async deleteAccount(): Promise<void> {
    const password = prompt('This permanently deletes your account. Enter your password to confirm:');
    if (!password) return;
    const res = await api.deleteAccount(password);
    if (res.success) {
      this.cb.onChange(false, null);
      this.showAuth();
      this.setStatus('Account deleted.');
    } else this.setAccStatus(res.message ?? 'Failed.', true);
  }
}
